import type { FitnessDay, FormZone } from '@painpal/contracts';
import { formZone } from '@painpal/core';
import type { FitnessRange } from '../../lib/dates';
import { formatSigned, formatWeekdayDayMonth } from '../../lib/format';
import { plural } from '../../lib/plural';
import { STRAVA_DAILY_BUDGET } from '../../lib/strava';

export interface TodayStats { today: FitnessDay; weekAgo: FitnessDay | null; zone: FormZone }

export function peakIndex(days: FitnessDay[]): number {
  let best = 0;
  let bestValue = -Infinity;
  days.forEach((day, index) => {
    if (day.fitness > bestValue) {
      best = index;
      bestValue = day.fitness;
    }
  });
  return best;
}

export function todayStats(days: FitnessDay[]): TodayStats | null {
  const today = days.at(-1);
  if (today === undefined) return null;
  return { today, weekAgo: days.at(-8) ?? null, zone: formZone(today.form) };
}

export function weekNote(now: number, before: number | undefined): string {
  return before === undefined ? 'TODAY' : `${formatSigned(now - before)} THIS WK`;
}

export function peakLabel(days: FitnessDay[]): string {
  const peak = days[peakIndex(days)];
  return peak === undefined ? '' : `PEAK FITNESS · ${Math.round(peak.fitness)}`;
}

export function importNote(daysLeft: number | null): string {
  const when = daysLeft === null ? 'over the next few days' : `in about ${daysLeft} ${plural(daysLeft, 'day', 'days')}`;
  return `Importing newest rides first. Strava allows ~${STRAVA_DAILY_BUDGET} requests a day, so the rest lands ${when}. Your chart fills in as they arrive.`;
}

export const defaultFitnessRange = (isPhone: boolean): FitnessRange => (isPhone ? '6w' : '3m');

export function todayNote(days: FitnessDay[]): string {
  const today = days.at(-1);
  if (today === undefined) return '';
  return `TODAY · ${Math.round(today.fitness)} / ${Math.round(today.fatigue)} / ${formatSigned(today.form)}`;
}

// Rounds first so the change matches the whole numbers printed beside it and in todayNote.
const delta = (now: number, before: number): string => {
  const signed = formatSigned(Math.round(now) - Math.round(before));
  return `(${signed === '0' ? '±0' : signed})`;
};

// Where the plan takes Fitness, Fatigue and Form by its last day, against today, and the planned days and Load that get there.
// `plannedDays` is /api/fitness's forecastPlannedDays: the forecast days alone can't tell a Rest day from an unplanned one.
export function projectionLine(days: FitnessDay[], forecast: FitnessDay[], plannedDays: number): string | null {
  const today = days.at(-1);
  const end = forecast.at(-1);
  if (today === undefined || end === undefined || end.date <= today.date) return null;
  const load = forecast.reduce((sum, day) => sum + day.load, 0);
  return [
    `PROJECTED · ${formatWeekdayDayMonth(end.date)}`,
    `FITNESS ${Math.round(end.fitness)} ${delta(end.fitness, today.fitness)}`,
    `FATIGUE ${Math.round(end.fatigue)} ${delta(end.fatigue, today.fatigue)}`,
    `FORM ${formatSigned(end.form)} ${delta(end.form, today.form)}`,
    `FROM ${plannedDays} PLANNED ${plural(plannedDays, 'DAY')} · ~${Math.round(load)} LOAD · UNPLANNED DAYS COUNT AS REST`,
  ].join(' · ');
}
